import { useState } from "preact/hooks"
import { SearchTermsFacet } from "@nosto/nosto-js/client"
import Checkbox from "@/elements/Checkbox/Checkbox"
import { useOptimisticFacet } from "@/hooks/useOptimisticFacet"
import { cl } from "@nosto/search-js/utils"
import styles from "./TermsFacet.module.css"

type Props = {
  facet: SearchTermsFacet
}

// Show the in-facet search box only for longer lists.
const SEARCHABLE_THRESHOLD = 8

export default function TermsFacet({ facet }: Props) {
  const { active, toggleActive, optimisticData, toggleProductFilter } = useOptimisticFacet(facet)
  const [search, setSearch] = useState("")

  // Highest quantity first, then narrow by the search box.
  const values = [...optimisticData]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .filter(value => value.value.toLowerCase().includes(search.toLowerCase().trim()))

  const searchable = optimisticData.length > SEARCHABLE_THRESHOLD

  return (
    <li className={cl(styles.facet, active && styles.active)}>
      <button
        className={styles.header}
        data-nosto-element="facet"
        onClick={toggleActive}
        aria-controls={`${facet.id}-sub-menu`}
        aria-expanded={!!active}
        aria-label={`${active ? "Collapse" : "Expand"} ${facet.name}`}
        type="button"
      >
        <span className={styles.title}>{facet.name}</span>
        <span className={styles.toggleIcon} aria-hidden="true">
          {active ? "−" : "+"}
        </span>
      </button>

      <div className={styles.menu} id={`${facet.id}-sub-menu`}>
        {searchable && (
          <form className={styles.search} onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search"
              value={search}
              onInput={e => setSearch((e.target as HTMLInputElement).value)}
              aria-label={`Search ${facet.name}`}
            />
            <button className={styles.searchButton} type="submit">
              Search
            </button>
          </form>
        )}

        <ul className={styles.options} role="menu">
          {values.map(value => (
            <li key={value.value}>
              <Checkbox
                className={cl(styles.option, value.selected && styles.optionSelected)}
                value={`${value.value} (${value.count})`}
                selected={value.selected}
                onChange={() => toggleProductFilter(facet.field, value.value, !value.selected)}
              />
            </li>
          ))}
        </ul>
      </div>
    </li>
  )
}
